import { submitFeedback } from '@/api/feedbackAPI'
import ErrorMessage from '@/components/ErrorMessage'
import LoginSignup from '@/components/LoginSignup'
import { Authenticated, Unauthenticated } from '@/utils/auth/auth'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { getEffectiveOrgUuid } from '@/utils/utils'
import {
  Box,
  Typography,
  Alert,
  FormControl,
  TextField,
  Button,
  CircularProgress,
  Tooltip,
} from '@mui/material'
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined'
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined'
import { useState } from 'react'
import { useFocusManagement } from '@/utils/hooks/useFocusManagement'
import { usePageTitle } from '@/utils/hooks/usePageTitle'

export default function FeedbackPage() {
  usePageTitle('Feedback Form')

  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [isPostive, setIsPostive] = useState<boolean | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const focusRef = useFocusManagement()

  const { user, token, isLoading: userLoading, orgs } = getCurrentUser()

  if (!userLoading && orgs && orgs.length === 0) {
    window.location.href = '/'
  }

  const effectiveOrgUuid = getEffectiveOrgUuid(user, orgs)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!effectiveOrgUuid || !token) {
      return
    }

    if (!subject.trim() || !message.trim()) {
      setError('Please fill out both subject and message')
      return
    }

    if (isPostive === null) {
      setError('Please select a feedback type')
      return
    }

    setError(null)
    setIsSubmitting(true)
    setSubmitted(false)

    try {
      const result = await submitFeedback({
        scan_result_id: null,
        org_uuid: effectiveOrgUuid,
        subject,
        message,
        hidden: isPostive === false ? true : false,
        token,
      })

      // Reset form
      setSubject('')
      setMessage('')
      setIsPostive(null)

      if (result) {
        setSubmitted(true)
      }
    } catch {
      setError('Failed to submit feedback')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <Unauthenticated>
        <LoginSignup />
      </Unauthenticated>
      <Authenticated>
        <>
          {!user || !token || (!effectiveOrgUuid && <ErrorMessage />)}

          <Box
            sx={{
              margin: '0 auto',
              maxWidth: '800px',
              pb: 2,
              px: 5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="h5"
              component="h1"
              ref={focusRef}
              tabIndex={-1}
              sx={{
                textAlign: 'center',
                mb: 1,
                fontWeight: 500,
                '&:focus': {
                  outline: 'none',
                },
              }}
            >
              Feedback Form
            </Typography>

            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              Sharing your feedback helps us improve our service.
            </Typography>
            {submitted && (
              <Alert severity="success" sx={{ mb: 2, alignItems: 'center' }}>
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{ textAlign: 'center' }}
                >
                  Feedback submitted! Thank you!
                </Typography>
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">
                  Error submitting feedback
                </Typography>
                <Typography variant="body2">{error}</Typography>
                <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                  If this problem persists, please contact support.
                </Typography>
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={(e) => {
                handleSubmit(e)
              }}
              sx={{ width: '100%' }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  justifyContent: 'center',
                  alignItems: 'center',
                  mb: 2,
                  mt: 2,
                }}
                role="group"
                aria-labelledby="feedback-type-label"
                aria-required="true"
              >
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Thumbs Up - Positive Feedback">
                    <Button
                      variant="contained"
                      disableRipple
                      aria-pressed={Boolean(isPostive)}
                      onClick={() => setIsPostive(true)}
                      sx={{
                        border: 2,
                        borderColor: isPostive ? '#0C74D4' : '#1C3045',
                        color: isPostive ? '#0C74D4' : '#1C3045',
                        backgroundColor: isPostive ? '#F6FCFE' : 'transparent',
                      }}
                    >
                      <ThumbUpAltOutlinedIcon />
                    </Button>
                  </Tooltip>

                  <Tooltip title="Thumbs Down - Negative Feedback">
                    <Button
                      variant="contained"
                      disableRipple
                      aria-pressed={isPostive === null ? false : !isPostive}
                      onClick={() => setIsPostive(false)}
                      sx={{
                        border: 2,
                        borderColor:
                          isPostive === false ? '#0C74D4' : '#1C3045',
                        color: isPostive === false ? '#0C74D4' : '#1C3045',
                        backgroundColor:
                          isPostive === false ? '#F6FCFE' : 'transparent',
                      }}
                    >
                      <ThumbDownAltOutlinedIcon />
                    </Button>
                  </Tooltip>
                </Box>
                <Typography
                  id="feedback-type-label"
                  variant="caption"
                  sx={{ color: 'text.secondary' }}
                >
                  Let us know the type of feedback you're submitting, so that we
                  can allocate it to the right place. *
                </Typography>
              </Box>

              <FormControl sx={{ width: '100%', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Subject *
                </Typography>
                <TextField
                  fullWidth
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g., Something's not working"
                  disabled={isSubmitting}
                  size="small"
                  autoComplete="off"
                  required
                  aria-required="true"
                  aria-label="Subject (required)"
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#757575',
                    },
                  }}
                />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Please provide more details *
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={12}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="More details about the issue"
                  disabled={isSubmitting}
                  required
                  aria-required="true"
                  aria-label="Please provide more details (required)"
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#757575',
                    },
                  }}
                />
                <Button
                  variant="contained"
                  disableRipple
                  type="submit"
                  disabled={
                    isSubmitting ||
                    !subject.trim() ||
                    !message.trim() ||
                    isPostive === null
                  }
                  sx={{
                    bgcolor: '#1C3045',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '16px',
                  }}
                >
                  {isSubmitting ? (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CircularProgress
                        size={20}
                        sx={{ color: 'white', mr: 1 }}
                      />
                      <span>Submitting...</span>
                    </Box>
                  ) : (
                    'Submit Feedback'
                  )}
                </Button>
              </FormControl>
            </Box>
          </Box>
        </>
      </Authenticated>
    </>
  )
}
